import { JsonLDArticle, JsonLDType } from '@cinch/oxd-components/json-ld/src'
import ArticlePage from '@cinch/oxd-pages/article/src'
import { Env, getEnv } from '@cinch/oxd-utils/env/src'
import htmlSerializer, { linkResolver } from '@somo/oxd-apps-www/src/utils/html-serializer'
import { graphql } from 'gatsby'
import { RichText } from 'prismic-reactjs'
import React from 'react'
import SEO, { Breadcrumb } from '../components/seo'

// IRIS have added these to EVERY document, so we need to strip them here.
// TODO: Remove this method once documents have been cleaned in prismic.
const stripCinch = (str: string) => str.replace(' | cinch', '')

interface ArticlePage extends Cinch.SitePage {
  data: any
}

const Page: React.FC<ArticlePage> = ({ data, location }) => {
  const { prismic } = data
  const doc = prismic.allArticles.edges.slice(0, 1).pop()

  if (!doc) {
    return null
  }

  const { node } = doc

  const relatedJargon =
    (node.related_jargons &&
      !!node.related_jargons.length &&
      node.related_jargons
        .filter(({ related_jargon }: any) => !!related_jargon)
        .map(({ related_jargon }: any) => ({
          question: related_jargon.question,
          summary: related_jargon.summary,
          uid: related_jargon._meta.uid,
          id: related_jargon._meta.id,
        }))) ||
    []

  const articleUrl = `${getEnv(Env.SiteUrl)}/post/${node._meta.uid}`

  const articleSchema: JsonLDArticle['schema'] = {
    author: node.author && {
      type: JsonLDType.Person,
      schema: {
        name: `${node.author.givenName} ${node.author.familyName}`,
      },
    },
    mainEntityOfPage: {
      type: JsonLDType.WebPage,
      schema: {
        id: articleUrl,
      },
    },
    headline: node.title,
    datePublished: node._meta.firstPublicationDate,
    dateModified: node._meta.lastPublicationDate,
    image: [node.main_image.url],
    description: RichText.asText(node.lead_paragraph || []) || node.sub_title,
    url: articleUrl,
  }

  const seoNode = node.body && node.body[0].primary

  const seoProps = {
    title: (seoNode && stripCinch(seoNode.title_tag)) || node.title,
    desc: (seoNode && seoNode.meta_description) || node.sub_title,
  }

  const breadcrumbs: Breadcrumb[] = []

  if (location) {
    const pageUrl = `${getEnv(Env.SiteUrl)}${location.pathname}`
    breadcrumbs.push(
      {
        item: `${getEnv(Env.SiteUrl)}/guides`,
        name: `How-to guides`,
      },
      {
        item: pageUrl,
        name: seoProps.title,
      },
    )
  }

  return (
    <>
      <SEO {...seoProps} breadcrumbs={breadcrumbs} articleSchema={articleSchema} />
      <ArticlePage
        author={node.author}
        title={node.title}
        subTitle={node.sub_title}
        leadParagraph={RichText.render(node.lead_paragraph || [], linkResolver, htmlSerializer)}
        quote={node.quote}
        body={RichText.render(node.article_body || [], linkResolver, htmlSerializer)}
        featuredImage={node.main_image}
        relatedJargon={relatedJargon}
        createdAt={node._meta.firstPublicationDate}
      />
    </>
  )
}

export const query = graphql`
  query ArticleQuery($uid: String) {
    prismic {
      allArticles(uid: $uid) {
        edges {
          node {
            body {
              ... on PRISMIC_ArticleBodySeo {
                primary {
                  title_tag
                  meta_description
                }
              }
            }
            author {
              ... on PRISMIC_Author {
                givenName: given_name
                familyName: family_name
                occupation
                bio
                photo {
                  ... on PRISMIC__ImageLink {
                    url
                    name
                  }
                }
              }
            }
            title
            sub_title
            main_image
            lead_paragraph
            quote
            article_body
            related_jargons {
              related_jargon {
                ... on PRISMIC_Jargon {
                  question
                  summary
                  _meta {
                    uid
                    id
                  }
                }
              }
            }
            _meta {
              uid
              firstPublicationDate
              lastPublicationDate
            }
          }
        }
      }
    }
  }
`

export default Page
